import { SubscriptionTier } from './services/billing.service';

/**
 * Display catalog for the subscription plans, shared by the signup plan picker
 * (/select-plan) and Settings → Billing so the two can't drift apart.
 *
 * Display only — what a customer is actually charged is the Stripe price, and
 * the limits that are enforced come from TIER#<tier>/CONFIG. Keep these values
 * in step with both (and with the marketing site's pricing section).
 */
export interface PlanCard {
  tier: SubscriptionTier;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  /** Self-serve purchasable through Stripe Checkout. */
  selfServe: boolean;
  /**
   * Listed in the plan grid. CUSTOM is `isPublic: false` in the tier catalog,
   * so it is rendered only when it is the org's current plan.
   */
  listed: boolean;
}

export const PLAN_CATALOG: readonly PlanCard[] = [
  {
    tier: 'FREE',
    name: 'Free',
    price: '$0',
    period: '',
    description: 'Get started with the essentials.',
    features: ['1 monitored service', 'Owner only (1 seat)', 'All integrations', 'Email & Slack notifications'],
    selfServe: false,
    listed: true,
  },
  {
    tier: 'PRO',
    name: 'Pro',
    price: '$29',
    period: '/month',
    description: 'For growing engineering teams.',
    features: ['10 monitored services', 'Up to 5 team members', 'Ticket routing (Jira & GitHub)', 'Priority support'],
    selfServe: true,
    listed: true,
  },
  {
    tier: 'ENTERPRISE',
    name: 'Enterprise',
    price: '$99',
    period: '/month',
    description: 'Scale-out coverage for large orgs.',
    features: ['50 monitored services', 'Up to 25 team members', 'All Pro features', 'Dedicated support'],
    selfServe: true,
    listed: true,
  },
  {
    // Admin-assigned only. Never listed as an option; shown solely so an org
    // already on CUSTOM sees its real plan instead of falling back to Free.
    tier: 'CUSTOM',
    name: 'Custom',
    price: 'Negotiated',
    period: '',
    description: 'Tailored limits and pricing agreed with your account team.',
    features: ['Custom monitored-service limit', 'Custom team size', 'All Enterprise features', 'Dedicated support'],
    selfServe: false,
    listed: false,
  },
];
